import { Bell, RefreshCw, CheckCheck } from 'lucide-react'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { useNotificationStore } from '@/stores/notification.store'
import type { Notification } from '@/types/api/notification'

import { useNotificationActions } from '../hooks/useNotificationActions'

import { BrowserNotificationsToggle } from './BrowserNotificationsToggle'
import { NotificationRow } from './NotificationRow'

export function NotificationsPage() {
  const {
    notifications,
    unreadCount,
    hasMore,
    isLoading,
  } = useNotificationStore()

  const {
    refresh,
    loadMore,
    isLoadingMore,
    markAllAsRead,
    markRead,
    dismiss,
  } = useNotificationActions()

  const navigate = useNavigate()

  useEffect(() => {
    void refresh(true)
  }, [refresh])

  /** Notifications with a link (automation notifications) open what they are about. */
  const open = (notification: Notification) => {
    if (!notification.link) return
    void markRead(notification)
    navigate(notification.link)
  }

  const unread = notifications.filter(
    (notification) => !notification.read
  )

  const earlier = notifications.filter(
    (notification) => notification.read
  )

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">
            Notifications
          </h1>

          {unreadCount > 0 && (
            <p className="mt-0.5 text-sm text-neutral-500">
              {unreadCount} unread
            </p>
          )}
        </div>

        <div className="flex gap-2">
          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void markAllAsRead()
              }}
            >
              <CheckCheck className="mr-2 h-4 w-4" />
              Mark all read
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void refresh(true)
            }}
            disabled={isLoading}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${
                isLoading ? 'animate-spin' : ''
              }`}
            />
            Refresh
          </Button>
        </div>
      </div>

      <BrowserNotificationsToggle className="text-sm text-muted-foreground" />

      {/* Empty */}
      {notifications.length === 0 &&
        !isLoading && (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-neutral-200 bg-white py-16 text-neutral-400">
            <Bell className="h-10 w-10" />
            <p className="text-sm">
              No notifications in this workspace yet
            </p>
          </div>
        )}

      {/* Loading */}
      {isLoading &&
        notifications.length === 0 && (
          <div className="space-y-2">
            {[1, 2, 3].map((item) => (
              <div
                key={item}
                className="h-16 animate-pulse rounded-lg bg-neutral-100"
              />
            ))}
          </div>
        )}

      {/* Unread */}
      {unread.length > 0 && (
        <section>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-neutral-400">
            Unread
          </h2>

          <div className="divide-y divide-neutral-100 overflow-hidden rounded-lg border border-neutral-200 bg-white">
            {unread.map((notification) => (
              <NotificationRow
                key={notification.id}
                notification={notification}
                onClick={notification.link ? open : undefined}
                onDelete={dismiss}
              />
            ))}
          </div>
        </section>
      )}

      {/* Earlier */}
      {earlier.length > 0 && (
        <section>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-neutral-400">
            Earlier
          </h2>

          <div className="divide-y divide-neutral-100 overflow-hidden rounded-lg border border-neutral-200 bg-white">
            {earlier.map((notification) => (
              <NotificationRow
                key={notification.id}
                notification={notification}
                onClick={notification.link ? open : undefined}
                onDelete={dismiss}
              />
            ))}
          </div>
        </section>
      )}

      {hasMore && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void loadMore()
            }}
            disabled={isLoadingMore}
          >
            {isLoadingMore && <RefreshCw className="mr-2 h-4 w-4 animate-spin" />}
            Load more
          </Button>
        </div>
      )}
    </div>
  )
}
